import { useCallback, useEffect, useState } from "react";
import { Popover } from "@mui/material";
import { TbBell, TbX } from "react-icons/tb";
import moment from "moment";
import "moment/locale/pt-br";
import {
  getStoredSelectedAccountId,
  IB_ACTIVE_ACCOUNT_CHANGED_EVENT,
} from "../../../Constants/accountTypes";
import {
  fetchIbNotifications,
  fetchIbUnreadNotificationCount,
  markAllIbNotificationsRead,
  markIbNotificationRead,
} from "../../../Services/ibNotificationsApi";
import {
  HEADER_ACTION_BTN_CLASS,
  HEADER_ACTION_ICON_CLASS,
  HEADER_ACTION_ICON_SIZE,
} from "../HeaderActionButtons/headerActionStyles";

moment.locale("pt-br");

const POLL_MS = 60_000;

const formatRelativeTime = (createdAt: string | null) => {
  if (!createdAt) return "";
  const m = moment(createdAt);
  if (!m.isValid()) return "";
  return m.fromNow();
};

type NotificationItem = {
  id: string;
  title: string;
  message: string;
  createdAt: string | null;
  read: boolean;
  type?: string | null;
};

export const NotificationsPopover = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [activeAccountId, setActiveAccountId] = useState<string | null>(
    () => getStoredSelectedAccountId(),
  );

  const open = Boolean(anchorEl);
  const hasUnread = unreadCount > 0;

  const refreshUnread = useCallback(async () => {
    try {
      const count = await fetchIbUnreadNotificationCount();
      setUnreadCount(count);
    } catch {
      // Keep last known badge count on transient errors.
    }
  }, []);

  const refreshList = useCallback(async () => {
    setLoading(true);
    try {
      const items = await fetchIbNotifications(50);
      setNotifications(items);
      setUnreadCount(items.filter((n) => !n.read).length);
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Badge poll — re-subscribe when Conta changes (hard reload also remounts).
  useEffect(() => {
    void refreshUnread();
    const timer = window.setInterval(() => {
      void refreshUnread();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [refreshUnread, activeAccountId]);

  // Clear stale list/badge as soon as the header Conta changes.
  useEffect(() => {
    const onAccountChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ accountId?: string | null }>).detail;
      const nextId =
        detail?.accountId != null && detail.accountId !== ""
          ? String(detail.accountId)
          : getStoredSelectedAccountId();
      setActiveAccountId(nextId);
      setNotifications([]);
      setUnreadCount(0);
      void refreshUnread();
    };
    window.addEventListener(IB_ACTIVE_ACCOUNT_CHANGED_EVENT, onAccountChanged);
    return () =>
      window.removeEventListener(IB_ACTIVE_ACCOUNT_CHANGED_EVENT, onAccountChanged);
  }, [refreshUnread]);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    void refreshList();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkRead = async (id: string) => {
    const target = notifications.find((n) => n.id === id);
    if (!target || target.read) return;

    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));

    try {
      await markIbNotificationRead(id);
    } catch {
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: false } : n))
      );
      void refreshUnread();
    }
  };

  const handleMarkAllRead = async () => {
    if (!hasUnread || markingAll) return;
    setMarkingAll(true);
    const previous = notifications;
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await markAllIbNotificationsRead();
    } catch {
      setNotifications(previous);
      void refreshUnread();
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={HEADER_ACTION_BTN_CLASS}
        aria-label="Notificações"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={handleOpen}
      >
        <TbBell
          size={HEADER_ACTION_ICON_SIZE}
          className={HEADER_ACTION_ICON_CLASS}
          aria-hidden
        />
        {hasUnread && !open && (
          <span
            className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-gold-mid px-1 text-[9px] font-semibold leading-none text-white ring-2 ring-white dark:ring-brand-dark-elevated"
            aria-hidden
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          className: "app-card",
          sx: {
            mt: 1,
            width: 320,
            maxWidth: "95vw",
            overflow: "hidden",
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgb(0 0 0 / 0.08)",
          },
        }}
      >
        <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-brand-dark">Notificações</h2>
          <div className="flex items-center gap-1">
            {hasUnread && (
              <button
                type="button"
                onClick={() => void handleMarkAllRead()}
                disabled={markingAll}
                className="rounded px-2 py-1 text-[11px] font-medium text-brand-gold-mid transition hover:bg-brand-cream disabled:opacity-50"
              >
                Marcar todas como lidas
              </button>
            )}
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-50 hover:text-brand-dark"
              aria-label="Fechar notificações"
            >
              <TbX className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>

        {loading && notifications.length === 0 ? (
          <div className="app-empty-state m-4 !border-0 !bg-transparent !p-6 !shadow-none">
            Carregando…
          </div>
        ) : notifications.length === 0 ? (
          <div className="app-empty-state m-4 !border-0 !bg-transparent !p-6 !shadow-none">
            Nenhuma notificação
          </div>
        ) : (
          <ul className="max-h-80 overflow-y-auto" role="list">
            {notifications.map((notification) => (
              <li key={notification.id}>
                <button
                  type="button"
                  onClick={() => void handleMarkRead(notification.id)}
                  className={`w-full border-b border-gray-50 px-4 py-3 text-left transition last:border-b-0 hover:bg-gray-50 ${
                    notification.read
                      ? "bg-white"
                      : "border-l-2 border-l-brand-gold-mid bg-brand-cream/60"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p
                      className={`text-sm leading-tight ${
                        notification.read
                          ? "font-medium text-gray-600"
                          : "font-semibold text-brand-dark"
                      }`}
                    >
                      {notification.title}
                    </p>
                    <span className="shrink-0 text-[10px] text-gray-400">
                      {formatRelativeTime(notification.createdAt)}
                    </span>
                  </div>
                  <p
                    className={`mt-1 text-xs leading-snug ${
                      notification.read ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    {notification.message}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Popover>
    </>
  );
};
